import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Activity,
  Brain,
  Heart,
  Flame,
  Users,
  AlertTriangle,
  Sparkles,
  Loader2,
  CheckCircle2,
  Shield
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsTraumaPage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (traumaId, traumaName, content) => {
    setAiLoading({ ...aiLoading, [traumaId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_traumatologie',
          audience: 'adult',
          prompt: `Analyse traumatologique pour ${traumaName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Évaluation initiale et critères de gravité
2. Priorités thérapeutiques immédiates
3. Protocole de réanimation (ABCDE)
4. Critères d'orientation spécialisée
5. Pièges à éviter en pré-hospitalier`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [traumaId]: data.response });
        toast.success(`Analyse AI ${traumaName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [traumaId]: false });
    }
  };

  const protocols = [
    {
      id: 'choc-hemorragique',
      name: 'Choc Hémorragique Adulte',
      icon: Heart,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**CHOC HÉMORRAGIQUE DE L'ADULTE**

**DÉFINITION:**
État d'hypoperfusion résultant d'une perte sanguine significative

**SIGNES CLINIQUES:**
• Hypotension: PAS < 90 mmHg ou PAM 60-65 mmHg
• Tachycardie: FC > 100 bpm
• Signes d'hypoperfusion:
  - Pâleur cutanée
  - Altération conscience
  - Oligurie

**SCORE ABC (Gravité trauma):**
• FC (Fréquence Cardiaque)
• PAS (Pression Artérielle Systolique)
• Épanchement intra-abdominal
• Traumatisme pénétrant

**INDEX DE CHOC:**
• FC/PAS > 0,9 = Indicateur de gravité

**TRAITEMENT:**

**1. REMPLISSAGE VASCULAIRE:**

**Cristalloïdes (1ère intention):**
• **NaCl 0,9%:** 10-20 mL/kg
• **Maximum 1ère heure:** 40-60 mL/kg si choc
• **⚠️ Éviter remplissage massif > 2000 mL**
• Priorité à la transfusion précoce

**Objectifs tensionnels:**
• PAS ≥ 110 mmHg si TC grave
• PAM ≥ 80 mmHg
• Remplissage limité au strict nécessaire

**Réévaluation:**
• Après 2ème remplissage si hypotension persistante
• Initier 3ème remplissage + vasopresseurs

**2. VASOPRESSEURS:**

**NORADRÉNALINE (1ère intention):**
• **Indication:** PAM cible non atteinte après remplissage/transfusion
• **Voie:** Périphérique dédiée possible
• **Dose:** 0,5-2 mg/h IVSE
• **Titration:** ↑ 0,2-0,5 mg/h toutes les 15 min
• **Dilution:** 8 mg dans 40 mL = 0,2 mg/mL

**DOBUTAMINE:**
• Si bas débit cardiaque
• **Dose initiale:** 2 µg/kg/min
• **Titration:** ↑ 5-10 µg/kg/min

**ADRÉNALINE:**
• Situations particulières
• En association à la Noradrénaline

**3. TRANSFUSION:**

**CONCENTRÉS GLOBULAIRES ROUGES (CGR):**
• **10 mL/kg** pour ↑ 2 g/dL Hb
• **Objectif:**
  - Hb 9-10 g/dL si coronarien/TC grave
  - Hb 7-9 g/dL sinon

**PLASMA FRAIS CONGELÉ (PFC):**
• **Ratio:** 1:1 ou 1:2 avec CGR
• **10 mL/kg** pour ↑ 15-20% facteurs coagulation

**PLAQUETTES:**
• **Objectif:** ≥ 50 G/L
• **OU:** ≥ 100 G/L si trauma sévère/hémorragie active
• Envisager après 2ème CGR

**FIBRINOGÈNE:**
• **Dose initiale:** 3 g pour 70 kg
• **Objectif:** ≥ 1,5 g/L

**4. ACIDE TRANEXAMIQUE:**
• **Dose:** 1 g IV bolus sur 10 min
• **Puis:** 1 g perfusé sur 8h
• **Indication:** Choc hémorragique < 3h

**5. CALCIUM:**
• **Chlorure de calcium 10%:** 1 g
• **Voie séparée** de la transfusion
• **Objectif:** Calcémie ionisée ≥ 0,9 mmol/L

**6. ANTAGONISATION:**

**AVK:**
• **PPSB:** 25 UI/kg
• **+ Vitamine K:** 10 mg

**AOD (Rivaroxaban, Apixaban):**
• **PPSB:** 50 UI/kg
• Répétable après 8h

**DABIGATRAN:**
• **Idarucizumab (Praxbind®):** 5g
• 2ème dose possible
• Si indisponible: PPSB 50 UI/kg

**SURVEILLANCE:**
• Constantes vitales (FC, PA, SpO2, EtCO2)
• Glycémie, température
• GCS, pupilles
• Paramètres coagulation
• Syndrome compartimental si traction

**ORIENTATION:**
• Transport médicalisé
• Centre de traumatologie
• Régulation préalable`
    },
    {
      id: 'tc-grave',
      name: 'Traumatisme Crânien Grave',
      icon: Brain,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**TRAUMATISME CRÂNIEN GRAVE**

**DÉFINITION:**
• **Glasgow ≤ 8**

**CRITÈRES DE GRAVITÉ:**
• Polytraumatisme
• Choc associé
• Anisocorie
• Mydriase uni ou bilatérale aréactive
• Doppler transcrânien anormal (< 10 min)

**⚠️ ASSOCIATION FRÉQUENTE:**
Trauma cervical → Collier cervical systématique

**ÉVALUATION INITIALE:**
• GCS rapide
• Pupilles (taille, réactivité)
• Signes de latéralisation
• Doppler transcrânien si disponible

**INTUBATION/VENTILATION:**

**INDICATIONS:**
• GCS ≤ 8
• Détresse ventilatoire sévère
• Intubation difficile anticipée

**TECHNIQUE:**
• Alignement tête-cou-tronc
• Position neutre de la tête
• Légère surélévation si possible
• Sédation IVSE (éviter bolus hypotenseurs)

**OBJECTIFS HÉMODYNAMIQUES:**

**ACSOS STRICT:**
• **PAS ≥ 110 mmHg**
• **OU PAM ≥ 80 mmHg**

**⚠️ HYPOTENSION:**
• 5 minutes d'hypotension = Mortalité x 2
• Introduction rapide d'amines si hypotension

**OBJECTIFS VENTILATOIRES:**
• **EtCO2:** 30-35 mmHg
• **OU pCO2:** 35-40 mmHg (si gaz sanguin)
• **SpO2 ≥ 94%**

**OBJECTIFS MÉTABOLIQUES:**
• **Glycémie:** 1,4-2 g/L (8-11 mmol/L)
• **Traiter hypoglycémie** si < 0,6 g/L

**OSMOTHÉRAPIE:**

**MANNITOL 20%:**
• **Dose:** 250 mL sur 15-20 min (adulte)
• **OU:** 0,5-1 g/kg
• **Compensation:** diurèse osmotique par NaCl 0,9%

**INDICATIONS:**
• Signes d'HTIC:
  - Mydriase
  - Anisocorie
  - Doppler anormal

**CONTRÔLE TEMPÉRATURE:**
• **Objectif:** 35-37°C
• Éviter hyperthermie > 37,5°C

**SURVEILLANCE DURANT TRANSPORT:**
• **Pupilles:** Toutes les 15-30 min
• État neurologique (GCS)
• Hémodynamique
• Ventilation (EtCO2)
• Glycémie

**ORIENTATION:**
• Réanimation chirurgicale
• **Neurochirurgie:** Rennes ou Brest
• Transport médicalisé
• Régulation tripartite

**⚠️ À SAVOIR:**
• Hypoxie et hypotension = Facteurs aggravants majeurs
• Neuroprotection = Priorité absolue
• Osmothérapie précoce si signes HTIC`
    },
    {
      id: 'polytrauma',
      name: 'Polytraumatisé - Prise en Charge Initiale',
      icon: Users,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**PRISE EN CHARGE INITIALE DU POLYTRAUMATISÉ**

**RÉGULATION:**
• Activation primaire selon mécanisme lésionnel
• Évaluation équilibrée par médecin SMUR
• Conférence tripartite:
  - SMUR
  - Régulateur
  - Équipe chirurgicale

**GRADATION DE GRAVITÉ (ABC):**

**GRADE A (Critique):**
• Détresse respiratoire sévère (SpO2 < 90% sous O2)
• Hypotension (PAS < 90 mmHg après > 1000 mL)
• GCS ≤ 8
• Nécessité vasopresseurs
• Transfusion pré-hospitalière

**GRADE B (Urgent):**
• Détresse respiratoire stabilisée (SpO2 ≥ 90%)
• Hypotension corrigée (PAM ≥ 90 mmHg)
• 9 ≤ GCS ≤ 13
• Trauma pénétrant
• Trauma thoracique avec volet costal
• Trauma rachidien
• Trauma pelvien
• Amputation, délabrement, écrasement
• eFAST positif (hémopéritoine, hémothorax, hémopéricarde)

**GRADE C (À surveiller):**
• Chute hauteur (adulte ≥ 6m, enfant ≥ 3m)
• Victime éjectée/écrasée
• Décès dans même véhicule
• Jugement clinique urgentiste

**PRISE EN CHARGE MÉDICALE:**

**ÉVALUATION ABCDE:**

**A - AIRWAY (Voies aériennes):**
• Libération, protection
• Immobilisation cervicale
• Intubation si nécessaire

**B - BREATHING (Respiration):**
• Oxygène haute concentration
• Ventilation assistée/contrôlée
• Drainage si pneumothorax

**C - CIRCULATION:**
• 2 VVP gros calibre
• Remplissage NaCl 0,9%
• Transfusion précoce si choc
• Vasopresseurs si besoin
• Contrôle hémorragie externe

**D - DISABILITY (Neurologique):**
• GCS
• Pupilles
• Signes de latéralisation

**E - EXPOSURE (Exposition):**
• Déshabillage complet
• Examen corps entier
• Prévention hypothermie

**ÉCHOGRAPHIE EMBARQUÉE:**
• eFAST si disponible
• Recherche:
  - Hémopéritoine
  - Hémothorax
  - Hémopéricarde
  - Pneumothorax

**PRODUITS SANGUINS:**
• Départ avec CGR si possible
• Anticipation besoins transfusionnels

**DAMAGE CONTROL:**
• Évaluation rapide sur site
• **Objectif:** Transfert rapide (Golden Hour)
• Traitement définitif = Chirurgie
• Stabilisation minimale nécessaire

**ORIENTATION:**

**SELON GRAVITÉ:**
• **Grade A:** Réanimation/Chirurgie
• **Grade B:** USC/Chirurgie
• **Grade C:** SAU surveillance

**CENTRES SPÉCIALISÉS:**
• Centre traumatologie
• Neurochirurgie (TC grave)
• Centre grands brûlés (brûlures graves)

**TRANSPORT:**
• Médicalisé systématique
• Régulation préalable
• Préparation équipe receveuse

**⚠️ PRINCIPES CLÉS:**
• Treat first what kills first
• Damage control resuscitation
• Golden hour respectée
• Hypothermie = Ennemi
• Communication équipe++`
    },
    {
      id: 'brule-grave',
      name: 'Brûlé Grave - Stratégie Pré-Hospitalière',
      icon: Flame,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-400/30',
      content: `**BRÛLÉ GRAVE - STRATÉGIE PRÉ-HOSPITALIÈRE**

**DÉFINITION BRÛLURE GRAVE:**

**ADULTE:**
• SCB > 20%
• 3ème degré > 5%
• Inhalation fumées
• Localisations profondes spécifiques:
  - Face, mains, pieds, périnée
• Brûlure électrique haute tension

**OU:**
• SCB < 20% avec:
  - Âge > 75 ans
  - Comorbidités sévères
  - Inhalation
  - Brûlure circulaire
  - Brûlures localisées profondes
  - SCB > 10%
  - 3ème degré 3-5%
  - Électrique basse tension
  - Chimique

**ENFANT:**
• SCB > 10%
• 3ème degré > 5%
• Nourrisson < 1 an
• Comorbidités sévères
• Inhalation
• Brûlures localisées profondes
• Brûlure circulaire
• Électrique ou chimique

**ÉVALUATION SURFACE CORPORELLE BRÛLÉE (SCB):**

**RÈGLE DES 9 (Adulte):**
• Tête/cou: 9%
• Membre supérieur: 9% x 2
• Tronc antérieur: 18%
• Tronc postérieur: 18%
• Membre inférieur: 18% x 2
• Périnée: 1%

**APPLICATION E-BURN:**
• St Luc-St Joseph
• Évaluation précise SCB

**ÉVALUATION PROFONDEUR:**

**1er DEGRÉ (Non comptabilisé):**
• Érythème seulement

**2ème DEGRÉ SUPERFICIEL:**
• Phlyctènes tendues
• Humide
• Douloureux++
• Vitropression positive
• Présence poils

**2ème DEGRÉ PROFOND:**
• Phlyctènes flasques
• Moins humide
• Douleur modérée
• Vitropression douteuse
• Poils arrachables

**3ème DEGRÉ:**
• Sec, cartonné
• Indolore
• Pas vitropression
• Absence poils

**TRAITEMENT:**

**REFROIDISSEMENT:**
• Eau tempérée (15-25°C)
• Maximum 10-15 minutes
• Arrêter si hypothermie/frissons
• ⚠️ Pas de glace directe

**PANSEMENT:**
• Retirer vêtements brûlés (sauf adhérents)
• Pansement stérile sec
• OU champs stériles humides tièdes
• Emballage film alimentaire si rien d'autre
• Surélévation membres

**ANALGÉSIE:**
• **Morphine:** Titration
• Doses répétées selon douleur
• Voie IV préférentielle

**REMPLISSAGE (Formule de Parkland modifiée):**

**1ÈRE HEURE:**
• **20 mL/kg** cristalloïdes
• **Ringer Lactate** en priorité
• OU NaCl 0,9% si indisponible

**APRÈS 1ÈRE HEURE:**
• **10 mL/kg/%SCB par heure**
• Jusqu'à avis centre brûlés

**Exemple:** Patient 70 kg, SCB 30%
• 1ère heure: 1400 mL
• Puis: 210 mL/h (%SCB x poids/10)

**INTUBATION:**

**INDICATIONS:**
• Détresse respiratoire
• Inhalation massive fumées
• Brûlures faciales circonférentielles
• GCS altéré
• Prévention œdème VAS

**PARTICULARITÉS:**
• IOT précoce si doute inhalation
• Taille sonde adaptée (œdème possible)

**SURVEILLANCE:**
• Constantes (FC, PA, SpO2, temp)
• Diurèse (objectif 0,5-1 mL/kg/h)
• État neurologique
• Douleur
• Extension brûlures
• Syndrome compartimental

**ORIENTATION:**

**CENTRE GRANDS BRÛLÉS:**
• Selon critères de gravité
• Régulation avec centre
• Transport médicalisé
• Maintien remplissage
• Analgésie continue

**⚠️ COMPLICATIONS PRÉCOCES:**
• Choc hypovolémique
• Hypothermie
• Syndrome compartimental
• Rhabdomyolyse
• Insuffisance rénale
• Œdème lésionnel

**⚠️ À SAVOIR:**
• Hypothermie = Ennemi n°1
• Remplissage = Clé survie
• Analgésie = Obligatoire
• Centre spécialisé = Orientation précoce`
    },
    {
      id: 'pendaison',
      name: 'Pendaison',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**PENDAISON**

**DÉFINITION:**
• **Complète:** Corps entièrement suspendu
• **Incomplète:** Partie du corps touche le sol

**CONSÉQUENCES PHYSIOPATHOLOGIQUES:**

**CERVICALES:**
• Fracture cervicale
• Compression médullaire

**RESPIRATOIRES:**
• Hypoxie par obstruction VAS
• Œdème glottique
• OAP post-obstructif

**NEUROLOGIQUES:**
• HTIC (obstruction jugulaire)
• Ischémie cérébrale (dissection carotide)

**CARDIAQUES:**
• Arrêt cardiaque

**CONDUITE À TENIR:**

**PROTECTION RACHIS CERVICAL:**
• **Maintien tête systématique**
• **Collier cervical rigide**
• Immobilisation rachis complet
• Alignement tête-cou-tronc

**SI ARRÊT RESPIRATOIRE:**
• Réanimation standard ACR hypoxique
• **Priorité:** Oxygénation
• 5 insufflations initiales
• MCE standard
• Défibrillation si nécessaire

**SI GCS ≥ 8:**
• VVP
• Oxygène haute concentration
• Monitoring complet (scope, SpO2, PA)
• Surveillance neurologique
• **Envisager intubation** selon clinique

**SI GCS < 8:**

**INTUBATION:**
• **Après induction séquence rapide (ISR)**
• Particularités:
  - Maintien rachis cervical
  - Envisager sonde plus petite (œdème)
  - Anticiper intubation difficile

**ALTERNATIVE SI ÉCHEC:**
• **Cricothyroïdotomie** si:
  - Trauma cranio-facial
  - Œdème glottique
  - Ventilation/intubation impossible

**CONTRÔLE TEMPÉRATURE:**
• **Éviter T°C > 37,5°C**
• Refroidissement si fièvre
• Neuroprotection

**GESTION ACSOS:**
(Cf protocole TC grave)

• **PAS ≥ 110 mmHg**
• **PAM ≥ 80 mmHg**
• **SpO2 ≥ 94%**
• **EtCO2:** 30-35 mmHg
• **Glycémie:** 1,4-2 g/L

**OSMOTHÉRAPIE SI HTIC:**

**Signes d'HTIC:**
• Mydriase
• Anisocorie
• Doppler transcrânien anormal

**MANNITOL 20%:**
• **Dose:** 250 mL sur 15-20 min (adulte)
• **OU:** 0,5-1 g/kg
• **Compensation:** Diurèse par NaCl 0,9%

**SURVEILLANCE:**
• État neurologique (GCS, pupilles)
• Fonction respiratoire
• Hémodynamique
• Température
• Lésions cervicales
• Recherche complications:
  - OAP post-obstructif
  - HTIC
  - Ischémie cérébrale

**ORIENTATION:**
• Transport médicalisé
• **Réanimation** ou déchocage
• Selon gravité clinique
• Maintien alignement tête-cou-tronc
• Immobilisation complète

**SI DÉCÈS CONSTATÉ:**
• ACR > 30 minutes
• Rigidité cadavérique
• **Arrêt réanimation**
• **Obstacle médico-légal systématique**
• Préserver les lieux
• Attendre autorités

**⚠️ COMPLICATIONS:**
• OAP lésionnel post-obstructif
• Ischémie cérébrale irréversible
• Lésions médullaires cervicales
• Pneumonie d'inhalation

**⚠️ À SAVOIR:**
• Protection rachis = Priorité absolue
• Intubation précoce si doute
• Neuroprotection stricte
• Obstacle médico-légal toujours
• Support psychologique équipes/témoins`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-xl border border-red-400/30">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Référentiels Traumatologie</h1>
            <p className="text-[hsl(var(--text-dim))]">Protocoles urgences traumatiques - SMUR 2025</p>
          </div>
        </div>
        
        <Alert className="bg-red-500/10 border-red-400/30">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <strong>Golden Hour :</strong> Trauma sévère = Transfert rapide vers centre spécialisé
          </AlertDescription>
        </Alert>
      </div>

      {/* Main Content */}
      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-[hsl(var(--text))]">
            <Activity className="w-5 h-5 text-red-400" />
            Protocoles Traumatologiques
          </CardTitle>
          <CardDescription>
            Sélectionnez un protocole pour afficher les recommandations détaillées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {protocols.map((protocol) => {
                const Icon = protocol.icon;
                const isLoading = aiLoading[protocol.id];
                const aiResult = aiResults[protocol.id];

                return (
                  <AccordionItem
                    key={protocol.id}
                    value={protocol.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                  >
                    <AccordionTrigger
                      className="px-6 py-4 hover:no-underline hover:bg-[hsl(var(--bg-elev))] transition-colors"
                      data-testid={`accordion-trigger-${protocol.id}`}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className={`p-2 ${protocol.bg} rounded-lg border ${protocol.border}`}>
                          <Icon className={`w-5 h-5 ${protocol.color}`} />
                        </div>
                        <span className="font-semibold text-[hsl(var(--text))] text-left">
                          {protocol.name}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 py-4 space-y-4">
                      <div className="space-y-4">
                        {/* Contenu médical */}
                        <div className="prose prose-invert max-w-none">
                          <div className="text-sm text-[hsl(var(--text))] whitespace-pre-wrap leading-relaxed">
                            {protocol.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return (
                                  <h3 key={idx} className="text-lg font-semibold text-cyan-400 mt-4 mb-2">
                                    {line.replace(/\*\*/g, '')}
                                  </h3>
                                );
                              }
                              if (line.startsWith('• ') || line.startsWith('- ')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 my-1">
                                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              if (line.startsWith('⚠️')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              return <p key={idx} className="my-1">{line}</p>;
                            })}
                          </div>
                        </div>

                        {/* Bouton Analyse AI */}
                        <Button
                          onClick={() => handleAiAnalysis(protocol.id, protocol.name, protocol.content)}
                          disabled={isLoading}
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                          data-testid={`ai-button-${protocol.id}`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyse AI en cours...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Analyse AI de ce protocole
                            </>
                          )}
                        </Button>

                        {/* Résultat AI */}
                        {aiResult && (
                          <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                              <Sparkles className="w-5 h-5" />
                              Analyse AI
                            </h3>
                            <div className="prose prose-invert max-w-none">
                              <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${protocol.id}`}>
                                {aiResult}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
